import { useState } from 'react'
import { useAppSelector } from '../../hooks/useAppDispatch'
import { useGetPublicLegalDocumentsQuery } from '../../store/api/legalApi'
import { useApplyDeveloperMutation } from '../../store/api/developerApi'
import { api, getErrorMessage } from '../../lib/apiClient'
import { ENDPOINTS } from '../../lib/endpoints'
import { TextInput, Textarea } from '../../components/ui'

// Shown the first time a user opens /developer, before any DeveloperApplication
// exists. Collects the review form (org name, website, reason) and — same as
// onboarding's mandatory-terms step — requires downloading and ticking the
// Creator Agreement before the application can be submitted.
export default function DeveloperApplyForm({ onSubmitted }: { onSubmitted: () => void }) {
  const { accessToken } = useAppSelector((s) => s.auth)
  const { data: legalData } = useGetPublicLegalDocumentsQuery()
  const [applyDeveloper, { isLoading: submitting }] = useApplyDeveloperMutation()

  const [orgName, setOrgName] = useState('')
  const [website, setWebsite] = useState('')
  const [reason, setReason] = useState('')
  const [hasDownloaded, setHasDownloaded] = useState(false)
  const [agreedCheckbox, setAgreedCheckbox] = useState(false)
  const [downloading, setDownloading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const developerDoc = legalData?.documents.find((d) => d.type === 'developer')
  const docUploaded = !!developerDoc?.filename

  const handleDownload = async () => {
    setDownloading(true)
    setError(null)
    try {
      const { blob, filename } = await api.downloadFile(
        'users',
        ENDPOINTS.legal.download('developer'),
        accessToken,
      )
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = filename ?? 'creator-agreement.pdf'
      document.body.appendChild(a)
      a.click()
      a.remove()
      URL.revokeObjectURL(url)
      setHasDownloaded(true)
    } catch (err) {
      setError(getErrorMessage(err, 'Download failed — please try again'))
    } finally {
      setDownloading(false)
    }
  }

  const handleSubmit = async () => {
    setError(null)
    if (!orgName.trim() || !reason.trim()) {
      setError('Organization name and reason are required')
      return
    }
    try {
      await applyDeveloper({
        orgName: orgName.trim(),
        website: website.trim() || undefined,
        reason: reason.trim(),
        agreedToTerms: true,
      }).unwrap()
      onSubmitted()
    } catch (err) {
      setError(getErrorMessage(err, 'Failed to submit your application — please try again'))
    }
  }

  const canSubmit = orgName.trim() && reason.trim() && agreedCheckbox

  return (
    <div style={{ maxWidth: 520, margin: '2rem auto', padding: '0 1rem' }}>
      <div style={{ background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 16, padding: '1.75rem' }}>
        <div style={{ fontSize: '2.25rem', marginBottom: '0.5rem' }}>🤖</div>
        <h2 style={{ margin: '0 0 0.4rem', fontSize: '1.25rem', fontWeight: 700, color: 'var(--text-primary)' }}>
          Become a creator on Ekkam
        </h2>
        <p style={{ margin: '0 0 1.25rem', color: 'var(--text-muted)', lineHeight: 1.6, fontSize: '0.9rem' }}>
          Build and publish AI agents that answer questions automatically. Tell us a bit about you, then
          review and agree to the Creator Agreement to submit your application.
        </p>

        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.85rem', marginBottom: '1.1rem' }}>
          <TextInput label="Organization / Team Name" value={orgName} onChange={(e) => setOrgName(e.target.value)} placeholder="e.g. Acme AI" />
          <TextInput label="Website (optional)" value={website} onChange={(e) => setWebsite(e.target.value)} placeholder="https://..." />
          <Textarea label="Why do you want to build agents on Ekkam?" value={reason} onChange={(e) => setReason(e.target.value)} rows={3} />
        </div>

        {!docUploaded ? (
          <div style={{ padding: '0.75rem 0.85rem', borderRadius: 10, background: 'rgba(234,179,8,0.08)', border: '1px solid rgba(234,179,8,0.25)', fontSize: '0.85rem', color: 'var(--text-secondary)' }}>
            The Creator Agreement hasn't been published yet — please check back shortly.
          </div>
        ) : (
          <>
            <label
              style={{
                display: 'flex',
                alignItems: 'flex-start',
                gap: '0.65rem',
                marginBottom: '1.1rem',
                padding: '0.75rem 0.85rem',
                borderRadius: 10,
                background: agreedCheckbox ? 'rgba(124,58,237,0.08)' : 'transparent',
                border: `1px solid ${agreedCheckbox ? 'rgba(124,58,237,0.35)' : 'var(--border)'}`,
                cursor: hasDownloaded ? 'pointer' : 'not-allowed',
                opacity: hasDownloaded ? 1 : 0.6,
                transition: 'all 0.15s',
              }}
            >
              <input
                type="checkbox"
                checked={agreedCheckbox}
                disabled={!hasDownloaded}
                onChange={(e) => setAgreedCheckbox(e.target.checked)}
                style={{ marginTop: '0.15rem', flexShrink: 0, width: 16, height: 16, accentColor: 'var(--accent-primary)', cursor: hasDownloaded ? 'pointer' : 'not-allowed' }}
              />
              <span style={{ fontSize: '0.85rem', lineHeight: 1.45, color: agreedCheckbox ? 'var(--text-primary)' : 'var(--text-secondary)' }}>
                I have downloaded and agree to the{' '}
                <a
                  href="#"
                  onClick={(e) => { e.preventDefault(); e.stopPropagation(); if (!downloading) void handleDownload() }}
                  style={{ color: 'var(--accent-primary)', fontWeight: 600, textDecoration: 'underline', textUnderlineOffset: '2px', cursor: downloading ? 'not-allowed' : 'pointer', pointerEvents: downloading ? 'none' : 'auto' }}
                >
                  Creator Agreement
                </a>
                {hasDownloaded && <span style={{ color: 'var(--success)' }}> ✓</span>}
              </span>
            </label>

            {error && (
              <div style={{ marginBottom: '1rem', padding: '0.6rem 0.75rem', borderRadius: 8, background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.25)', fontSize: '0.8rem', color: 'var(--error)' }}>
                {error}
              </div>
            )}

            <button
              onClick={() => void handleSubmit()}
              disabled={!canSubmit || submitting}
              style={{
                width: '100%',
                padding: '0.75rem',
                borderRadius: 10,
                fontWeight: 600,
                fontSize: '0.95rem',
                cursor: !canSubmit || submitting ? 'not-allowed' : 'pointer',
                background: !canSubmit || submitting ? 'transparent' : 'var(--accent-primary)',
                color: !canSubmit || submitting ? 'var(--text-muted)' : 'white',
                border: !canSubmit || submitting ? '1px solid var(--border)' : 'none',
                transition: 'all 0.15s',
              }}
            >
              {submitting ? 'Submitting…' : 'Submit Application →'}
            </button>
          </>
        )}
      </div>
    </div>
  )
}
